// Authoritative Jakin CDM pricing & proposal content.

export const SOCIAL_INTRO =
  "Consistent, strategic content designed to keep your brand visible, recognizable and connected to the people you serve.";

export const SOCIAL_PACKAGES = [
  {
    name: "Establish", price: "$497", cadence: "/month",
    bestFor: "Businesses that need a consistent, professional starting presence.",
    features: [
      "4 custom posts per month", "3 edited reels (client-provided footage)", "5 story frames",
      "Facebook and Instagram", "SEO-aware captions", "Monthly content calendar",
      "Page maintenance", "Up to 15 min of engagement per week",
    ],
  },
  {
    name: "Essential", price: "$897", cadence: "/month",
    bestFor: "Businesses ready to become more active across multiple platforms.",
    features: [
      "10 custom posts per month", "8 edited reels (client-provided footage)", "10 story frames",
      "Up to 3 platforms", "Content planning", "SEO-aware captions", "Hashtag & audience research",
      "Page maintenance", "Up to 30 min of engagement per week", "Monthly performance snapshot",
    ],
  },
  {
    name: "Expand", price: "$1,497", cadence: "/month", featured: true,
    bestFor: "Established businesses needing coordinated support beyond social.",
    features: [
      "15 custom posts per month", "10 edited reels", "Up to 3 platforms",
      "Google Business Profile management", "1 email campaign or blog per month",
      "Up to 2 hrs of website updates per month", "5 supporting graphic designs per month",
      "One 30-min monthly content session", "Up to 45 min of engagement per week", "Monthly analytics report",
    ],
  },
  {
    name: "Flourish", prefix: "Starting at", price: "$2,997", cadence: "/month",
    bestFor: "Businesses needing comprehensive creative & digital management.",
    features: [
      "20 custom posts per month", "15 edited reels", "Up to 5 platforms",
      "Google Business Profile management", "2 email campaigns or blogs per month",
      "Up to 4 hrs of website updates per month", "One 1-hr monthly photo & video session",
      "Up to 10 supporting graphic designs per month", "Up to 1 hr of engagement per week",
      "Monthly strategy session", "Monthly analytics report",
    ],
  },
];

export const SOCIAL_NOTES = [
  "Content is cross-posted and adapted for the selected platforms. Original platform-specific content, extensive on-site filming, influencer outreach, inbox customer service and paid-ad management may require a custom quote.",
  "Advertising spend is separate from the monthly management fee and is paid directly by the client.",
  "Reels are produced from client-provided footage. Reels requiring on-site filming, plus original platform-specific content beyond cross-posting, are quoted separately.",
];

export const FUNNEL_NOTE =
  "Funnel builds are one-time projects. Ongoing care and optimization are available separately.";

export const FUNNEL_INTRO =
  "Strategic, done-for-you systems that help your business capture leads, nurture interest and create a clear path toward booking or purchasing.";

export const FUNNEL_BUILDS = [
  {
    name: "Lead Capture Funnel", price: "$1,750", cadence: "one-time setup",
    bestFor: "Service businesses that want to collect leads and generate consultation requests.",
    features: [
      "Funnel strategy & customer journey", "One landing page", "Thank-you / confirmation page",
      "Lead magnet integration", "Lead delivery automation", "Three-email welcome sequence",
      "Consultation or booking integration", "Basic CRM integration & lead tagging",
      "Meta Pixel or Google Analytics connection", "Conversion tracking", "Mobile optimization",
      "Complete funnel testing", "Two revision rounds", "14 days of post-launch support",
    ],
    goal: "Capture leads and encourage them to book a consultation.",
  },
  {
    name: "Client Conversion Funnel", price: "$2,997", cadence: "one-time setup", featured: true, badge: "Most Popular",
    bestFor: "Businesses selling a higher-value service, coaching program, package or consultation.",
    features: [
      "Everything in the Lead Capture Funnel", "Long-form sales or service page",
      "Application, booking or checkout pathway", "Five-to-seven-email nurture sequence",
      "Follow-up for leads who don't book", "CRM pipeline setup", "Advanced lead tagging & segmentation",
      "SMS integration when supported", "Retargeting pixel setup", "Booking reminders",
      "Conversion dashboard", "30 days of post-launch support",
    ],
    goal: "Nurture interested leads and move them toward purchasing, applying or booking.",
  },
  {
    name: "Launch & Scale Funnel", prefix: "Starting at", price: "$4,997", cadence: "one-time setup",
    bestFor: "Courses, memberships, webinars, digital products, launches or complex sales processes.",
    features: [
      "Complete funnel strategy & mapping", "Multi-step funnel", "Webinar, VSL or launch pathway",
      "Registration & confirmation pages", "Sales or checkout page", "Upsell / next-step page",
      "Eight-to-twelve-email sequence", "Lead segmentation", "Advanced CRM automation",
      "Checkout or payment integration", "Retargeting pixel setup", "Conversion dashboard",
      "A/B testing framework", "Complete mobile testing", "Up to three revision rounds",
      "45 days of post-launch support",
    ],
    goal: "Manage the journey from initial interest through registration, purchase and follow-up.",
  },
];

export const FUNNEL_CARE = [
  {
    name: "Funnel Care", price: "$297", cadence: "/month",
    features: [
      "Monthly funnel-function check", "Form & automation testing", "Tracking review",
      "One minor page or automation update per month", "Monthly lead & conversion summary",
      "Up to 1 hr of support per month",
    ],
  },
  {
    name: "Funnel Optimization", price: "$597", cadence: "/month",
    features: [
      "Everything in Funnel Care", "Monthly conversion review", "Funnel drop-off analysis",
      "Up to 3 hrs of updates per month", "One page, CTA or email test per month",
      "Email-sequence refinements", "Monthly strategy recommendations",
    ],
  },
];

export const FUNNEL_CARE_NOTE =
  "CRM, email, SMS, domain, hosting, payment-processing and advertising expenses are separate client costs. Advertising management and ad creative require a separate agreement.";

export const ESTABLISH_CONVERT = {
  label: "Signature System",
  name: "Establish & Convert",
  subtitle: "Social Visibility + Lead Capture System",
  setup: "$2,250",
  price: "$1,997",
  cadence: "/month",
  description: "For businesses that need consistent social media visibility and a clear system for turning that attention into leads and consultation bookings.",
  groups: [
    { title: "Phase One — Lead Capture Funnel", items: ["Funnel strategy & customer journey", "Landing page", "Thank-you page", "Lead magnet integration", "Lead-delivery automation", "Three-email welcome sequence", "Consultation-booking integration", "Basic CRM integration", "Lead tagging", "Conversion tracking", "Complete funnel testing", "Two revision rounds"] },
    { title: "Ongoing Social Media Management", items: ["10 strategic posts per month", "8 edited reels (client-provided footage)", "10 story frames", "Up to 3 platforms", "SEO-aware captions", "Monthly content planning", "Up to 30 min of engagement per week", "Monthly analytics report"] },
    { title: "Ongoing Funnel Care", items: ["Monthly funnel-function check", "Form & automation testing", "Lead-count tracking", "Consultation-conversion tracking", "Funnel drop-off review", "One minor funnel update per month"] },
  ],
  goal: "This system is designed to create a clearer path from social media visibility to lead capture and consultation bookings.",
};

export const PHOTOGRAPHY = [
  { name: "Headshot Session", price: "$275", features: ["30-min studio or outdoor session", "5 professionally retouched images", "1 outfit change", "Online gallery", "Print & digital release"] },
  { name: "Mini Brand Session", price: "$375", features: ["30-minute session", "1 location", "10–15 professionally edited high-res images", "Up to 2 outfits", "Online gallery", "Commercial-use release"] },
  { name: "Half-Day Event Coverage", price: "$950", features: ["Up to 4 hours", "Candid & posed photography", "40+ professionally edited highlights", "Online gallery", "High-res print & web files"] },
  { name: "Establish Brand Session", price: "$1,100", featured: true, features: ["Up to 2 hours", "Studio + up to 2 nearby locations", "20–50 professionally edited images", "Up to 4 outfits", "Up to 1 hour of recorded footage", "Online gallery"] },
  { name: "Spotlight Video", prefix: "Starting at", price: "$1,350", features: ["Pre-production consultation", "Script or outline support", "Up to 3 filming hours", "Professional editing", "Color correction & music", "30-second teaser", "Mini photo session", "2 revision rounds"] },
  { name: "Full-Day Event Coverage", price: "$1,750", features: ["Up to 8 hours", "150+ professionally edited highlights", "2 short event recap videos", "Online gallery", "High-res print & web files"] },
];

export const VIDEO_ADDONS = [
  { name: "Additional filming", price: "$175 / hour" },
  { name: "Additional edit or format", price: "from $100" },
  { name: "Additional revision round", price: "$75" },
  { name: "Extended travel / specialized equipment", price: "Custom quote" },
];

export const WEBSITES = [
  { name: "One-Page Website", prefix: "Starting at", price: "$850", features: ["Strategic single-page layout", "Mobile optimization", "Service overview", "Biography", "Imagery", "Contact information", "Call to action", "Basic SEO settings", "2 revision rounds"] },
  { name: "Three-Page Website", prefix: "Starting at", price: "$1,350", features: ["Home page", "About page", "Services or Contact page", "Mobile optimization", "Basic SEO", "Contact form", "Social links", "2 revision rounds"] },
  { name: "Five-Page Website", prefix: "Starting at", price: "$1,950", featured: true, features: ["Up to 5 custom pages", "Mobile optimization", "Contact or lead form", "Basic SEO", "Branded visual system", "Standard integrations", "2 revision rounds"] },
  { name: "Advanced Website", prefix: "Starting at", price: "$2,750", features: ["For booking, membership or e-commerce", "Blog & database", "Advanced integrations", "Scope & timeline confirmed after consultation"] },
];

export const WEBSITE_CARE = {
  name: "Website Care", price: "$149", cadence: "/month",
  features: ["Up to 2 hrs of standard updates", "Link & form check", "Image & text changes", "Monthly website review"],
  note: "Platform subscriptions, hosting, domains, premium applications and third-party fees are separate.",
};

export const EMAIL = [
  { name: "Email Campaign", prefix: "Starting at", price: "$225", cadence: "per email", features: ["Strategy", "Copywriting", "Custom design", "CTA integration", "Scheduling", "Mobile review", "Analytics-ready setup"] },
  { name: "Monthly Email Package", price: "$797", cadence: "/month", featured: true, features: ["Up to 4 email campaigns", "Copywriting & design", "Scheduling", "Basic list organization", "Monthly performance snapshot"] },
  { name: "Welcome Sequence", prefix: "Starting at", price: "$750", cadence: "one-time setup", features: ["Up to 5 automated emails", "Copywriting", "Custom design", "Automation setup", "Testing", "One revision round"] },
];

export const YOUTUBE = [
  { name: "YouTube Support", price: "$697", cadence: "/month", features: ["Up to 2 client-provided videos", "Editing", "Custom thumbnails", "Titles, descriptions & keywords", "Upload scheduling", "Basic channel maintenance"] },
  { name: "YouTube Management", price: "$1,197", cadence: "/month", featured: true, features: ["Up to 4 client-provided videos", "Editing", "Custom thumbnails", "SEO optimization", "Upload scheduling", "Up to 4 Shorts repurposed from long-form", "Monthly performance snapshot"] },
  { name: "YouTube Growth", prefix: "Starting at", price: "$1,797", cadence: "/month", features: ["Monthly strategy", "Up to 4 edited long-form videos", "Up to 8 Shorts", "Custom thumbnails", "YouTube SEO", "Publishing", "Playlist & channel optimization", "Monthly analytics"] },
];

export const GRAPHIC_DESIGN = [
  { name: "Business Card", price: "from $85" },
  { name: "Postcard / Rack Card", price: "from $95" },
  { name: "Flyer / Poster", price: "from $125" },
  { name: "Door Hanger", price: "from $125" },
  { name: "Banner / Signage", price: "from $150" },
  { name: "Brochure", price: "from $225" },
  { name: "Logo Design", price: "from $450" },
  { name: "Photo Retouching", price: "$20–$45 / image" },
  { name: "Design Support", price: "$75 / hour" },
];

export const DESIGN_NOTES = [
  "Design deliverables include custom design, print-ready PDF where applicable, PNG and JPG files, and two revision rounds unless otherwise stated.",
  "Logo Design includes a consultation, two-to-three initial concepts, three revision rounds, SVG/PDF/PNG/JPG files, and color and black-and-white versions.",
  "Printing, shipping and vendor-production costs are separate unless specifically included in the approved quote.",
];

export const PRESENTATION_INTRO =
  "Strategic, professionally designed presentations tailored to your content, goals and audience. Choose the level of support that best fits your needs — from polishing an existing deck to developing a complete, high-impact presentation.";

export const PRESENTATION = [
  {
    name: "Presentation Polish", prefix: "Starting at", price: "$450",
    bestFor: "Clients with a complete deck and finalized content who need it professionally polished.",
    features: [
      "Up to 10 existing slides", "Final copy & content supplied by client", "Improved layout & visual consistency",
      "Typography & spacing improvements", "Existing brand colors & fonts", "Client-provided or licensed stock imagery",
      "Editable PowerPoint or Google Slides file", "PDF export", "Two revision rounds",
    ],
    foot: "Additional slides: $45 each. Does not include extensive copywriting, content research, custom charts, advanced animation or custom AI imagery.",
  },
  {
    name: "Custom Presentation", prefix: "Starting at", price: "$850",
    bestFor: "Clients who have their information but need help organizing it into a polished, branded deck.",
    features: [
      "Planning consultation", "Up to 15 custom slides", "Content organization", "Light copy editing",
      "Custom branded presentation design", "Licensed stock-image selection", "Basic icons, charts & diagrams",
      "Up to 2 custom AI-generated images", "Editable PowerPoint or Google Slides file", "PDF export", "Two revision rounds",
    ],
    foot: "Additional slides: $65 each.",
  },
  {
    name: "Signature Presentation", prefix: "Starting at", price: "$1,350", featured: true, badge: "Most Popular",
    bestFor: "Sales decks, sponsorships, workshops, training and speaking engagements needing stronger storytelling.",
    features: [
      "Strategy & discovery session", "Up to 20 custom slides", "Structure & storyline development",
      "Copy & message refinement", "Fully customized branded design", "Custom charts, diagrams & visual storytelling",
      "Up to 5 custom AI-generated images", "Professional transitions & light animation", "Speaker talking points",
      "Editable PowerPoint or Google Slides file", "PDF export", "Three revision rounds",
    ],
    foot: "Additional slides: $85 each.",
  },
  {
    name: "Premier Presentation", prefix: "Starting at", price: "$1,950", badge: "Premium Experience",
    bestFor: "Investor pitches, keynotes, executive meetings and other high-stakes presentations.",
    features: [
      "In-depth strategy & discovery session", "Up to 25 custom slides", "Complete presentation architecture",
      "Copywriting & message development", "Premium custom visual direction", "Advanced charts & data visualization",
      "Up to 10 coordinated AI-generated visuals", "Professional animations & transitions", "Speaker notes or full script",
      "One rehearsal / camera-confidence coaching session", "Editable PowerPoint + Google Slides", "PDF export",
      "Three revision rounds", "Priority project communication",
    ],
    foot: "Additional slides: $100 each.",
  },
];

export const PRESENTATION_ADDONS = [
  { name: "Custom AI-generated image", price: "$50 / image" },
  { name: "Set of 5 coordinated AI visuals", price: "$200" },
  { name: "Data-heavy / infographic slide", price: "from $125" },
  { name: "Additional revision round", price: "$75" },
  { name: "Speaker notes or scriptwriting", price: "from $250" },
  { name: "Rehearsal / camera coaching", price: "$175 / session" },
  { name: "Reusable presentation template", price: "from $650" },
  { name: "Presentation-to-video conversion", price: "from $350" },
  { name: "Rush scheduling", price: "+ $100" },
  { name: "Overnight delivery", price: "+ $100" },
];

export const PRESENTATION_NOTES = [
  "Presentation pricing depends on slide count, content readiness, research requirements, copywriting, data complexity, custom graphics, AI-generated imagery, animation and turnaround time.",
  "Clients must provide finalized data, statistics, citations and proprietary information unless research or content development is specifically included in the approved scope.",
  "AI image generation includes creative direction, concept development, prompt creation, image selection, refinement and preparation for presentation use. Complex composites, extensive revisions or highly specific photorealistic scenes may require a custom quote.",
  "Rush scheduling and overnight delivery are separate services. If a project requires both priority scheduling and next-business-day delivery, both $100 fees may apply — and availability must be confirmed in writing before the project begins.",
  "Final pricing, deliverables and turnaround time will be confirmed in the signed proposal or statement of work.",
];

export const DELIVERY = [
  { name: "Standard Delivery", price: "Included", desc: "The timeline is confirmed in writing before work begins and depends on the scope of work and the timely delivery of client materials." },
  { name: "Rush Service", price: "+ $100", desc: "Priority scheduling for work requested faster than the standard quoted turnaround. Availability must be confirmed by Jakin before purchase." },
  { name: "Overnight Delivery", price: "+ $100", desc: "Delivery by the next business day when the complete request and all materials are received by the agreed cutoff. Availability must be confirmed by Jakin." },
];

export const DELIVERY_NOTE =
  "Rush and overnight delivery are separate services. If a project requires both priority scheduling and overnight delivery, both $100 fees may apply. These fees are added to the regular project price. Delivery is not guaranteed until availability is confirmed in writing by Jakin Creative Design & Marketing.";

export const GENERAL_TERMS = [
  "Recurring retainers are due before the service month begins.",
  "A 50% deposit may be required for individual projects.",
  "All sales are final. Services, retainers, deposits and completed work are non-refundable. Limited exceptions may be considered at Jakin's sole discretion, but all transactions are considered final.",
  "Packages include the number of revisions specifically listed.",
  "Additional revisions are billed at $75 per hour unless otherwise quoted.",
  "Client delays, incomplete materials or substantial scope changes may affect delivery dates.",
  "Printing, advertising, domains, hosting, premium applications and third-party platform fees are separate unless specifically included.",
  "Custom packages are available.",
  "Final pricing and deliverables are confirmed in the signed proposal or statement of work.",
  "Marketing services cannot guarantee a specific number of followers, leads, sales, bookings, revenue or other business outcomes.",
];

export const AGREEMENT = [
  { h: "Engagement & Terms", p: "This agreement is between Jakin Creative Design & Marketing (\u201cJakin,\u201d \u201cwe,\u201d \u201cus\u201d) and the client named in the signed proposal. Recurring services are provided on a month-to-month retainer basis. Project-based work is governed by the scope and price in the approved proposal or statement of work." },
  { h: "Payments & Invoicing", p: "Retainers are billed in advance and are due before the service month begins; an invoice is sent approximately two days prior. Project deposits (up to 50%) are due before work begins, with the balance due per the agreed schedule. Overdue invoices may result in the suspension of services until the balance is paid." },
  { h: "Refunds & Final Sale", p: "All sales are final. Services, monthly retainers, project deposits and completed work are non-refundable once work has begun or the service period has started. Limited exceptions may be considered at the sole discretion of Jakin Creative Design & Marketing, but all transactions are otherwise considered final." },
  { h: "Termination", p: "Either party may end the engagement with written notice by email to jakincdm@gmail.com. Upon termination, Jakin will deliver the completed and paid-for photos, videos and files for your business, along with permission to use them." },
  { h: "Client Responsibilities & Approvals", p: "The client agrees to provide required materials, information and content approvals in a timely manner. Delays in providing materials or approvals may affect delivery dates. Content is created based on the approved plan and calendar for each month." },
  { h: "Communication & Calendar", p: "Each month the client receives a content calendar outlining planned posts, reels, captions, stories, travel dates and non-working days. This keeps communication clear and expectations aligned. Changes can be requested by email, phone or text at (941) 232-5985, or during the monthly and mid-month check-ins." },
  { h: "Revisions & Scope Changes", p: "Each package includes the number of revisions listed. Additional revisions or work outside the agreed scope are billed at $75 per hour unless otherwise quoted." },
  { h: "Rush & Overnight Fees", p: "Rush Service and Overnight Delivery are optional add-ons at $100 each. Both fees may apply when both services are requested. These fees are additional to the project price and are only confirmed once availability is approved in writing by Jakin." },
  { h: "Third-Party Expenses", p: "CRM, email, SMS, domains, hosting, premium applications, payment processing, printing and advertising spend are separate client costs unless specifically included in the approved quote." },
  { h: "Portfolio Use & Licensing", p: "With your consent, Jakin may showcase work created for your business in our portfolio, website and social media, including tags, collaboration links and backlinks to your channels. Ownership and licensing of final deliverables are transferred as described in the approved proposal." },
  { h: "Results Disclaimer", p: "Marketing and creative services do not guarantee any specific number of followers, leads, sales, bookings, revenue or other business outcomes." },
  { h: "Licensing & Compliance", p: "Jakin holds the licenses necessary to perform the work and will provide all services in compliance with applicable federal, state and local laws." },
];
